// Client Portal — Private LLM chat portal. Extracted from App.js.
import { useState, useEffect, useRef } from "react";
import { Loader2, Send, Server } from "lucide-react";
import { N8N_BASE } from "../lib/api";
import { isAllowedWebhookUrl } from "../lib/utils";

export const PrivateLLMChat = ({ facility, user }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Connected to your private LLM. This is a dedicated model hosted exclusively for ${facility.facility_name}.`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(
    () => `llm-${facility.facility_id}-${user.email}-${Date.now()}`,
  );
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const config =
    (facility.services || []).find((s) => s.service_type === "private_llm")
      ?.config || {};

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((p) => [
      ...p,
      { role: "user", content: text, timestamp: new Date() },
    ]);
    setInput("");
    setSending(true);

    // Try facility-specific LLM webhook, or fall back to model/query
    const fallbackUrl = `${N8N_BASE}/webhook/llm-chat/${facility.facility_id}`;
    const webhookUrl =
      config.chatbot_webhook_url &&
      isAllowedWebhookUrl(config.chatbot_webhook_url)
        ? config.chatbot_webhook_url
        : fallbackUrl;
    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatInput: text,
          sessionId,
          action: "sendMessage",
          facility_id: facility.facility_id,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content:
            data.output || data.text || data.response || JSON.stringify(data),
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content: `Error: ${err.message}. The Private LLM service may not be provisioned yet.`,
          timestamp: new Date(),
          isError: true,
        },
      ]);
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-amber-600 text-white flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Server className="w-5 h-5" />
          <div>
            <h3 className="text-sm font-semibold">
              {facility.facility_name} Private LLM
            </h3>
            <p className="text-xs text-amber-200">
              {config.ollama_model || "Dedicated hosted model"}
            </p>
          </div>
        </div>
        {config.rate_limit_daily && (
          <span className="text-xs text-amber-200">
            {config.rate_limit_daily} queries/day
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                msg.role === "user"
                  ? "bg-amber-600 text-white rounded-br-sm"
                  : msg.isError
                    ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                    : "bg-white text-slate-700 shadow-sm border border-slate-200 rounded-bl-sm"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 bg-white rounded-2xl rounded-bl-sm shadow-sm border border-slate-200">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                <div
                  className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <div
                  className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="px-4 py-3 border-t border-slate-200 flex space-x-3">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())
          }
          placeholder="Ask your private model..."
          disabled={sending}
          className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className={`p-2.5 rounded-xl ${sending || !input.trim() ? "bg-slate-200 text-slate-400" : "bg-amber-600 text-white hover:bg-amber-700"}`}
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DATA MAP PORTAL — client-facing datamap viewer
// ═══════════════════════════════════════════════════════════════════
